import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Mail, Smartphone } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { MenuItem, MenuPopover, MenuSeparator } from '@/components/ui/Popover';
import { MobilePayRequestConfirm } from '@/components/payments/MobilePayRequestConfirm';
import { SendClinioPayLink } from '@/components/payments/SendClinioPayLink';
import { usePatient } from '@/hooks/usePatients';
import { primaryTelephoneNumber } from '@/lib/patientStamkortFields';
import {
  useInvoiceMobilePayRequest,
  useMobilePayRequestGate,
} from '@/hooks/useInvoiceMobilePayRequest';
import { formatMoney, useInvoice, useSendInvoiceEmail } from '@/hooks/useInvoices';
import { hasValidPatientEmail } from '@/lib/calendarPaymentChoice';
import { mobilePayRequestBlock } from '@/lib/mobilePayRequest';
import { canOfferPaymentRequest } from '@/lib/paymentRequests';
import { contactFromSnapshot } from '@/lib/payLinkDelivery';

/** Staff send menu. Hidden unless the invoice can still take a payment request. */
export function InvoiceSendMenu({
  clinicId,
  invoiceId,
  rowsOnly = false,
}: {
  clinicId: string;
  invoiceId: string;
  /** Rows for Flere muligheder. The visit card uses the dropdown. */
  rowsOnly?: boolean;
}) {
  const { t } = useTranslation();
  const { data: invoice } = useInvoice(invoiceId);
  const { data: patient } = usePatient(invoice?.patient_id);
  const sendInvoiceEmail = useSendInvoiceEmail();
  const requestMobilePay = useInvoiceMobilePayRequest();
  const mobilePayGate = useMobilePayRequestGate(clinicId);
  const [mobilePayConfirm, setMobilePayConfirm] = useState(false);

  if (!invoice) return null;
  const remaining = Math.max(0, invoice.total_cents - (invoice.total_paid_cents ?? 0));
  if (!canOfferPaymentRequest({ outstandingCents: remaining, status: invoice.status })) return null;

  const snapshotContact = contactFromSnapshot(invoice.patient_snapshot);
  const emailRecipient = (patient?.email ?? snapshotContact.email)?.trim() ?? '';
  const patientPhone = primaryTelephoneNumber(patient?.telephone_subscribers) ?? snapshotContact.phone;
  const hasPhone = Boolean(patientPhone?.trim());
  const patientName = `${patient?.first_name ?? ''} ${patient?.last_name ?? ''}`.trim() || invoice.patient_id;
  const mobilePayBlock = mobilePayRequestBlock({
    ready: mobilePayGate.ready,
    mobilePayEnabled: mobilePayGate.mobilePayEnabled,
    clinioPayAvailable: mobilePayGate.clinioPayAvailable,
    outstandingCents: remaining,
  });

  async function sendInvoiceByEmail() {
    if (!hasValidPatientEmail(emailRecipient)) {
      toast.error(t('appointments.payment_choice.errors.missing_email'));
      return;
    }
    try {
      await sendInvoiceEmail.mutateAsync({ invoice_id: invoiceId, recipient: emailRecipient });
      toast.success(t('appointments.payment_choice.invoice_sent'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('appointments.payment_choice.errors.email_failed'));
    }
  }

  async function sendMobilePayRequest() {
    try {
      await requestMobilePay.mutateAsync(invoiceId);
      setMobilePayConfirm(false);
      toast.success(t('appointments.payment_choice.mobilepay_sent'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('appointments.payment_choice.errors.mobilepay_failed'));
    }
  }

  const rows = (
    <>
        <p className="px-2.5 pb-1 pt-2 text-[13px] leading-5 text-ds-muted" data-send-recipient="">
          {hasValidPatientEmail(emailRecipient)
            ? t('payments.send_to', { email: emailRecipient })
            : t('payments.pay_link_email_missing')}
        </p>
        <MenuSeparator />
        <div data-send-pay-link="">
          <SendClinioPayLink
            menuRows
            clinicId={clinicId}
            invoiceId={invoiceId}
            invoiceNumber={invoice.invoice_number}
            patientId={invoice.patient_id}
            email={patient?.email ?? snapshotContact.email}
            phone={patientPhone}
            patientName={patientName}
            smsConsent={patient?.sms_consent}
            consentLoaded={Boolean(patient)}
          />
        </div>
        {(!mobilePayGate.ready || mobilePayGate.mobilePayEnabled) && (
          <MenuItem
            icon={<Smartphone className="h-4 w-4" aria-hidden />}
            disabled={!hasPhone || requestMobilePay.isPending}
            data-send-mobilepay=""
            description={
              <>
                <span className="block">{t('invoices.mobilepay_request_note')}</span>
                {!hasPhone ? <span className="block">{t('payments.pay_link_phone_missing')}</span> : null}
              </>
            }
            onClick={() => setMobilePayConfirm(true)}
          >
            {t('payments.send_row_mobilepay')}
          </MenuItem>
        )}
        <MenuItem
          icon={
            sendInvoiceEmail.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Mail className="h-4 w-4" aria-hidden />
            )
          }
          disabled={sendInvoiceEmail.isPending || !hasValidPatientEmail(emailRecipient)}
          data-send-invoice-email=""
          description={
            <>
              <span className="block">{t('payments.send_row_invoice_email_hint')}</span>
              {hasValidPatientEmail(emailRecipient) ? null : (
                <span className="block">{t('payments.pay_link_email_missing')}</span>
              )}
            </>
          }
          onClick={() => {
            void sendInvoiceByEmail();
          }}
        >
          {t('payments.send_row_invoice_email')}
        </MenuItem>
    </>
  );

  const confirm = mobilePayConfirm ? (
    <div className="mt-2">
      <MobilePayRequestConfirm
        amountLabel={formatMoney(remaining, invoice.currency)}
        patientName={patientName}
        block={mobilePayBlock}
        ready={mobilePayGate.ready}
        busy={requestMobilePay.isPending}
        onCancel={() => setMobilePayConfirm(false)}
        onConfirm={() => {
          void sendMobilePayRequest();
        }}
      />
    </div>
  ) : null;

  if (rowsOnly) {
    return (
      <div data-invoice-send-menu="">
        {rows}
        {confirm}
      </div>
    );
  }

  return (
    <div data-invoice-send-menu="">
      <MenuPopover
        align="start"
        className="w-[min(22rem,calc(100vw-2rem))]"
        trigger={
          <Button type="button" variant="secondary" data-send-menu="">
            {t('payments.send_link_or_invoice')}
            <ChevronDown className="h-4 w-4" aria-hidden />
          </Button>
        }
      >
        {rows}
      </MenuPopover>
      {confirm}
    </div>
  );
}
